import { useLayoutEffect, useRef } from "react";

/** Follow rendered content, including delayed layout/streaming updates, until
 * the reader scrolls up. Sending or opening a conversation resumes following. */
export function useAskAutoScroll(hasThread: boolean) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const threadRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLDivElement>(null);
  const following = useRef(true);
  const lastScrollTop = useRef(0);

  const followLatest = () => { following.current = true; };
  const scrollToLatest = () => {
    const scroll = scrollRef.current;
    if (!scroll || !following.current) return;
    // Instant updates avoid queued smooth scrolls fighting the reader.
    scroll.scrollTop = scroll.scrollHeight;
    lastScrollTop.current = scroll.scrollTop;
  };
  const onScroll = () => {
    const scroll = scrollRef.current;
    if (!scroll) return;
    if (scroll.scrollTop < lastScrollTop.current - 1) following.current = false;
    else if (scroll.scrollHeight - scroll.clientHeight - scroll.scrollTop <= 2) following.current = true;
    lastScrollTop.current = scroll.scrollTop;
  };

  useLayoutEffect(() => {
    const scroll = scrollRef.current;
    const thread = threadRef.current;
    const composer = composerRef.current;
    if (!hasThread || !scroll || !thread || !composer) return;

    const update = () => {
      // The floating composer can grow as the question wraps or the viewport
      // narrows. Reserve its measured height, its bottom inset, and a text gap.
      scroll.style.setProperty("--ask-composer-height", `${composer.getBoundingClientRect().height}px`);
      scrollToLatest();
    };
    update();
    if (typeof ResizeObserver === "undefined") return;
    let frame = 0;
    const observer = new ResizeObserver(() => {
      // Updating thread padding changes another observed box. Schedule the
      // write outside observer delivery as the multiline composer grows.
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    });
    observer.observe(thread);
    observer.observe(composer);
    observer.observe(scroll);
    return () => { observer.disconnect(); cancelAnimationFrame(frame); };
  }, [hasThread]);

  // Covers sends, restored chats (including equally tall threads), and answers
  // before paint. ResizeObserver covers growth that happens outside React.
  useLayoutEffect(() => { if (hasThread) scrollToLatest(); });

  return { scrollRef, threadRef, composerRef, onScroll, followLatest };
}
